// 現在地(パス)から「このページで何ができるか」を解決するための静的マップ。
// 未知のパスでも必ず汎用の説明にフォールバックする（S3: 今開いている情報の文脈把握）。

export interface AdmanRouteInfo {
  title: string;
  description: string;
  // ページ別の「よくある質問」チップ（F18-a）。AIは呼ばない・押すとそのまま送信できる文言のみ。
  suggestions?: string[];
}

interface AdmanRouteMapEntry {
  path: string;
  // true の場合 `${path}/xxx` のような子パス（動的セグメント）にも一致させる。
  matchChildren?: boolean;
  info: AdmanRouteInfo;
}

export const ADMAN_DEFAULT_ROUTE_INFO: AdmanRouteInfo = {
  title: 'Tasky',
  description: '広告制作・商品登録・生成物の確認ができるページです。',
  suggestions: ['バナー作って', '今月何枚つくった？', '使い方を教えて'],
};

// frontend/src/App.tsx のルート定義（PrivateRoute配下）に対応させている。
const ROUTE_MAP: AdmanRouteMapEntry[] = [
  {
    path: '/',
    info: {
      title: 'ホーム',
      description: '生成状況のサマリーを確認できます。',
      suggestions: ['今月何枚つくった？', '残りクレジットは？', 'バナー作って'],
    },
  },
  {
    path: '/home',
    info: {
      title: 'ホーム',
      description: '生成状況のサマリーを確認できます。',
      suggestions: ['今月何枚つくった？', '残りクレジットは？', 'バナー作って'],
    },
  },
  {
    path: '/recent-works',
    info: {
      title: '最近の生成物',
      description: '直近の生成物を一覧で確認できます。',
      suggestions: ['直近の生成物を見せて', '先週作ったバナーは？', '最近の生成数を教えて'],
    },
  },
  {
    path: '/dashboard',
    info: {
      title: 'ダッシュボード',
      description: '全体の利用状況を確認できます。',
      suggestions: ['今月の実績を教えて', '全体の利用状況は？', '使い方を教えて'],
    },
  },
  {
    path: '/chat/ai',
    info: {
      title: 'AIチャット',
      description: 'AIチャットに相談できます。',
      suggestions: ['バナー作って', '商品を登録して', 'ここで何ができるの？'],
    },
  },
  {
    path: '/chat/super-agent',
    info: {
      title: 'スーパーエージェント',
      description: '高度なAIエージェントに相談できます。',
      suggestions: ['スーパーエージェントって何ができるの？', 'バナー作って', '商品を登録して'],
    },
  },
  {
    path: '/chat/new',
    info: {
      title: '新しいチャット',
      description: '新しいチャットを開始できます。',
      suggestions: ['バナー作って', '商品を登録して', '何を相談すればいい？'],
    },
  },
  {
    path: '/chat',
    matchChildren: true,
    info: {
      title: 'チャット',
      description: '過去のチャットの続きを確認できます。',
      suggestions: ['バナー作って', '商品を登録して', '今月何枚つくった？'],
    },
  },
  {
    path: '/profile',
    info: {
      title: 'プロフィール',
      description: 'アカウント情報を確認・編集できます。',
      suggestions: ['プロフィールで何が変えられる？', 'パスワードを変更したい', '通知設定はどこ？'],
    },
  },
  {
    path: '/account/settings',
    info: {
      title: 'アカウント設定',
      description: 'アカウントの各種設定を変更できます。',
      suggestions: ['アカウント設定で何ができる？', 'メールアドレスを変更したい', '退会するには？'],
    },
  },
  {
    path: '/usage',
    info: {
      title: '利用状況',
      description: 'クレジットや利用量を確認できます。',
      suggestions: ['今月のクレジット消費は？', '残りクレジットは？', '使いすぎてない？'],
    },
  },
  {
    path: '/ai-score',
    info: {
      title: 'AIスコア',
      description: 'クリエイティブのAIスコアを確認できます。',
      suggestions: ['AIスコアって何？', 'スコアを上げるには？', '自分のスコアを教えて'],
    },
  },
  {
    path: '/credits/purchase/success',
    info: {
      title: 'クレジット購入完了',
      description: 'クレジットの購入結果を確認できます。',
      suggestions: ['クレジットは反映されてる？', '残りクレジットは？', '購入履歴を見せて'],
    },
  },
  {
    path: '/credits/purchase/cancel',
    info: {
      title: 'クレジット購入キャンセル',
      description: 'クレジット購入がキャンセルされました。',
      suggestions: ['もう一度購入するには？', 'クレジットプランを教えて', '残りクレジットは？'],
    },
  },
  {
    path: '/organization/members',
    info: {
      title: '組織メンバー',
      description: '組織のメンバーを管理できます。',
      suggestions: ['メンバーを追加するには？', '権限の種類を教えて', '招待リンクが欲しい'],
    },
  },
  {
    path: '/organization/settings',
    info: {
      title: '組織設定',
      description: '組織の設定を変更できます。',
      suggestions: ['組織設定で何が変えられる？', '組織名を変更したい', '通知設定を変えたい'],
    },
  },
  {
    path: '/organization/analytics',
    info: {
      title: '組織の分析',
      description: '組織全体の利用分析を確認できます。',
      suggestions: ['組織全体の実績を教えて', '今月の利用状況は？', 'メンバー別の利用を見せて'],
    },
  },
  {
    path: '/organization/edit',
    info: {
      title: '組織情報の編集',
      description: '組織情報を編集できます。',
      suggestions: ['組織情報を編集したい', 'ロゴを変更するには？', '組織名を変更したい'],
    },
  },
  {
    path: '/organization',
    info: {
      title: '組織',
      description: '組織の情報を確認できます。',
      suggestions: ['組織の情報を教えて', 'メンバーを増やすには？', '組織設定を見せて'],
    },
  },
  {
    path: '/agents',
    info: {
      title: 'エージェント一覧',
      description: '利用できるAIエージェントを確認できます。',
      suggestions: ['どのエージェントを使えばいい？', 'おすすめのエージェントは？', 'バナー作って'],
    },
  },
  {
    path: '/document-creation',
    info: {
      title: '資料作成',
      description: 'AI資料を作成できます。',
      suggestions: ['資料を作って', 'どんな資料が作れる？', '商品を登録して'],
    },
  },
  {
    path: '/knowledge',
    info: {
      title: 'ナレッジ',
      description: '商品・ナレッジ情報を管理できます。',
      suggestions: ['商品を登録して', '商品一覧を見せて', '商品情報を更新したい'],
    },
  },
  {
    path: '/image-generation',
    info: {
      title: '画像生成',
      description: 'AIで画像を生成できます。',
      suggestions: ['画像を作って', 'おすすめのスタイルは？', 'バナー作って'],
    },
  },
  {
    path: '/ads/banner',
    info: {
      title: 'バナー広告',
      description: '商品からバナー広告を生成できます。',
      suggestions: ['バナー作って', '今月何枚つくった？', 'おすすめのスタイルある？', '商品を登録して'],
    },
  },
  {
    path: '/ads/carousel',
    info: {
      title: 'カルーセル広告',
      description: 'カルーセル広告を作成できます。',
      suggestions: ['カルーセル広告を作って', '何枚組で作れる？', '商品を登録して'],
    },
  },
  {
    path: '/ads/movie',
    matchChildren: true,
    info: {
      title: '動画広告',
      description: 'AIで動画広告を作成できます。',
      suggestions: ['動画広告を作って', '動画の作り方を教えて', '商品を登録して'],
    },
  },
  {
    path: '/ads/video',
    info: {
      title: '動画広告',
      description: '動画広告を作成できます。',
      suggestions: ['動画広告を作って', '動画の尺はどれくらい？', '商品を登録して'],
    },
  },
  {
    path: '/ads/manga',
    info: {
      title: '漫画広告',
      description: '漫画広告を作成できます。',
      suggestions: ['漫画広告を作って', '何コマまで作れる？', '商品を登録して'],
    },
  },
  {
    path: '/ads/lp',
    info: {
      title: 'LP作成',
      description: 'ランディングページを作成できます。',
      suggestions: ['LPを作って', 'LPの構成を教えて', '商品を登録して'],
    },
  },
  {
    path: '/ads/music',
    info: {
      title: '音楽生成',
      description: 'AIで音楽を生成できます。',
      suggestions: ['音楽を作って', 'どんな曲調が作れる？', '尺はどれくらい？'],
    },
  },
  {
    path: '/ads/song',
    matchChildren: true,
    info: {
      title: '歌広告',
      description: '歌広告（Song Ad）を作成できます。',
      suggestions: ['歌広告を作って', '歌広告の作り方を教えて', '商品を登録して'],
    },
  },
  {
    path: '/document-v2',
    info: {
      title: 'AI資料',
      description: 'AI資料を作成できます。',
      suggestions: ['資料を作って', 'AI資料でできることは？', 'テーマを変更したい'],
    },
  },
  {
    path: '/document-v3',
    matchChildren: true,
    info: {
      title: 'AI資料',
      description: 'AI資料を作成・編集できます。',
      suggestions: ['資料を作って', 'テーマを変更したい', 'AI資料でできることは？'],
    },
  },
  {
    path: '/video-document',
    matchChildren: true,
    info: {
      title: 'AI動画資料',
      description: 'スライド資料にAIナレーションを付けた動画資料を作成・編集できます。',
      suggestions: ['AI動画資料でできることは？', 'ナレーションの作り方は？', '使い方を教えて'],
    },
  },
  {
    path: '/standalone-knowledge',
    info: {
      title: 'ナレッジ',
      description: 'ナレッジ情報を確認できます。',
      suggestions: ['商品情報を確認したい', '商品を登録して', '商品一覧を見せて'],
    },
  },
  {
    path: '/ad-dashboard/meta',
    info: {
      title: '広告ダッシュボード（Meta）',
      description: 'Meta広告のパフォーマンスを確認できます。',
      suggestions: ['Meta広告の実績を教えて', '配信状況を見せて', '今月の成果は？'],
    },
  },
  {
    path: '/ad-dashboard/google',
    info: {
      title: '広告ダッシュボード（Google）',
      description: 'Google広告のパフォーマンスを確認できます。',
      suggestions: ['Google広告の実績を教えて', '配信状況を見せて', '今月の成果は？'],
    },
  },
  {
    path: '/ad-dashboard',
    info: {
      title: '広告ダッシュボード',
      description: '広告のパフォーマンスを確認できます。',
      suggestions: ['広告の実績を教えて', 'どの媒体が伸びてる？', '媒体連携のやり方を教えて'],
    },
  },
  {
    path: '/settings/media-connect',
    info: {
      title: '媒体連携設定',
      description: '広告媒体との連携を設定できます。',
      suggestions: ['媒体連携のやり方を教えて', '連携できる媒体は？', '連携が切れた時はどうする？'],
    },
  },
  {
    path: '/news',
    matchChildren: true,
    info: {
      title: 'お知らせ',
      description: 'お知らせの一覧・詳細を確認できます。',
      suggestions: ['最新のお知らせを教えて', 'アップデート情報が知りたい', '使い方を教えて'],
    },
  },
];

export const resolveAdmanRouteInfo = (pathname: string): AdmanRouteInfo => {
  const exact = ROUTE_MAP.find((entry) => entry.path === pathname);
  if (exact) return exact.info;

  const childMatches = ROUTE_MAP.filter(
    (entry) => entry.matchChildren && pathname.startsWith(`${entry.path}/`)
  ).sort((a, b) => b.path.length - a.path.length);

  if (childMatches.length > 0) return childMatches[0].info;

  return ADMAN_DEFAULT_ROUTE_INFO;
};
